import { z } from 'zod';

// Visual style and era
export const visualStylePresetEnum = z.enum([
  'cinematic_blockbuster',
  'cinematic_indie',
  'cinematic_noir',
  'cinematic_epic',
  'commercial_luxury',
  'commercial_lifestyle',
  'commercial_tech',
  'commercial_food',
  'commercial_automotive',
  'commercial_fashion',
  'commercial_beauty',
  'commercial_real_estate',
  'social_vertical_dynamic',
  'social_vertical_aesthetic',
  'social_ugc_authentic',
  'social_tiktok_trending',
  'documentary_observational',
  'documentary_interview',
  'documentary_nature',
  'documentary_journalistic',
  'artistic_experimental',
  'artistic_minimalist',
  'artistic_surreal',
  'artistic_vintage_film',
  'corporate_professional',
  'corporate_startup',
  'corporate_enterprise',
  'custom'
]);
export type VisualStylePreset = z.infer<typeof visualStylePresetEnum>;

export const visualEraEnum = z.enum([
  'contemporary_2024',
  'contemporary_near_future',
  'era_2010s',
  'era_2000s',
  'era_1990s',
  'era_1980s',
  'era_1970s',
  'era_1960s',
  'era_1950s',
  'era_1940s',
  'era_1930s',
  'era_1920s',
  'period_victorian',
  'period_edwardian',
  'period_art_deco',
  'period_renaissance',
  'period_medieval',
  'period_ancient',
  'future_near',
  'future_mid',
  'future_distant',
  'future_cyberpunk',
  'future_utopian',
  'future_dystopian',
  'timeless_classic',
  'timeless_modern',
  'custom'
]);
export type VisualEra = z.infer<typeof visualEraEnum>;

export const visualReferenceStyleEnum = z.enum([
  'ref_christopher_nolan',
  'ref_david_fincher',
  'ref_wes_anderson',
  'ref_denis_villeneuve',
  'ref_ridley_scott',
  'ref_spike_jonze',
  'ref_roger_deakins',
  'ref_emmanuel_lubezki',
  'ref_wong_kar_wai',
  'ref_terrence_malick',
  'ref_sofia_coppola',
  'ref_michael_mann',
  'ref_apple_commercial',
  'ref_nike_campaign',
  'ref_patagonia_outdoor',
  'ref_rolex_luxury',
  'ref_marvel_action',
  'ref_a24_indie',
  'ref_nature_documentary',
  'custom'
]);
export type VisualReferenceStyle = z.infer<typeof visualReferenceStyleEnum>;

// Color and mood
export const colorPaletteEnum = z.enum([
  'warm_golden',
  'warm_amber',
  'warm_orange',
  'cool_blue',
  'cool_teal',
  'cool_cyan',
  'neutral_balanced',
  'neutral_desaturated',
  'neutral_monochromatic',
  'high_contrast_vibrant',
  'high_contrast_complementary',
  'low_contrast_pastel',
  'low_contrast_earth',
  'palette_black_white',
  'palette_sepia',
  'palette_cyanotype',
  'palette_neon',
  'palette_jewel_tones',
  'palette_nordic',
  'palette_mediterranean',
  'palette_tropical',
  'palette_desert',
  'palette_urban',
  'palette_forest',
  'palette_ocean',
  'film_kodak_portra',
  'film_kodak_ektar',
  'film_fuji_velvia',
  'film_fuji_provia',
  'film_cinestill_800t',
  'film_kodak_vision3',
  'custom'
]);
export type ColorPalette = z.infer<typeof colorPaletteEnum>;

export const colorGradingEnum = z.enum([
  'grade_teal_orange_subtle',
  'grade_teal_orange_strong',
  'grade_blockbuster',
  'grade_indie_film',
  'grade_noir',
  'grade_bleach_bypass',
  'grade_cross_process',
  'grade_clean_commercial',
  'grade_social_vibrant',
  'grade_editorial',
  'grade_vintage_70s',
  'grade_vintage_80s',
  'grade_vintage_90s',
  'grade_dreamlike',
  'grade_gritty',
  'grade_cold_clinical',
  'grade_warm_nostalgic',
  'custom'
]);
export type ColorGrading = z.infer<typeof colorGradingEnum>;

export const moodAtmosphereEnum = z.enum([
  'mood_optimistic',
  'mood_joyful',
  'mood_serene',
  'mood_romantic',
  'mood_inspirational',
  'mood_playful',
  'mood_luxurious',
  'mood_confident',
  'mood_professional',
  'mood_documentary',
  'mood_contemplative',
  'mood_mysterious',
  'mood_dramatic',
  'mood_epic',
  'mood_suspenseful',
  'mood_action',
  'mood_urgent',
  'mood_melancholic',
  'mood_ominous',
  'mood_gritty',
  'mood_lonely',
  'mood_haunting',
  'mood_ethereal',
  'mood_surreal',
  'mood_nostalgic',
  'mood_fantastical',
  'custom'
]);
export type MoodAtmosphere = z.infer<typeof moodAtmosphereEnum>;

// Lighting
export const lightingSetupEnum = z.enum([
  'natural_golden_hour',
  'natural_blue_hour',
  'natural_midday_harsh',
  'natural_midday_diffused',
  'natural_window_soft',
  'natural_window_harsh',
  'natural_backlit',
  'natural_open_shade',
  'natural_dappled',
  'studio_soft_beauty',
  'studio_hard_dramatic',
  'studio_rembrandt',
  'studio_split',
  'studio_rim_backlit',
  'studio_butterfly',
  'studio_loop',
  'studio_broad',
  'studio_short',
  'studio_flat_even',
  'studio_clamshell',
  'studio_product_gradient',
  'studio_product_tent',
  'practical_tungsten',
  'practical_fluorescent',
  'practical_neon',
  'practical_led_mixed',
  'practical_firelight',
  'practical_candlelight',
  'practical_screen_glow',
  'practical_street_sodium',
  'practical_street_led',
  'cinematic_chiaroscuro',
  'cinematic_low_key',
  'cinematic_high_key',
  'cinematic_silhouette',
  'cinematic_motivated',
  'cinematic_unmotivated',
  'atmospheric_foggy',
  'atmospheric_hazy',
  'atmospheric_dusty',
  'atmospheric_smoky',
  'atmospheric_rainy',
  'custom'
]);
export type LightingSetup = z.infer<typeof lightingSetupEnum>;

export const lightQualityEnum = z.enum([
  'quality_hard',
  'quality_soft',
  'quality_diffused',
  'quality_specular',
  'quality_matte',
  'quality_mixed',
  'custom'
]);
export type LightQuality = z.infer<typeof lightQualityEnum>;

export const lightDirectionEnum = z.enum([
  'direction_front',
  'direction_front_45',
  'direction_side_90',
  'direction_back_rim',
  'direction_back_silhouette',
  'direction_top_down',
  'direction_bottom_up',
  'direction_three_point',
  'direction_ambient',
  'custom'
]);
export type LightDirection = z.infer<typeof lightDirectionEnum>;

export const lightingRatioEnum = z.enum([
  'ratio_1_1',
  'ratio_2_1',
  'ratio_4_1',
  'ratio_8_1',
  'ratio_16_1',
  'ratio_extreme',
  'custom'
]);
export type LightingRatio = z.infer<typeof lightingRatioEnum>;

// Shot and camera
export const shotSizeEnum = z.enum([
  'extreme_long_shot',
  'very_long_shot',
  'long_shot_wide',
  'long_shot_full',
  'medium_long_shot',
  'medium_shot',
  'medium_close_up',
  'close_up',
  'big_close_up',
  'extreme_close_up',
  'insert_shot',
  'cutaway',
  'two_shot',
  'three_shot',
  'group_shot',
  'over_shoulder',
  'point_of_view',
  'establishing',
  'custom'
]);
export type ShotSize = z.infer<typeof shotSizeEnum>;

export const shotPurposeEnum = z.enum([
  'establishing_wide',
  'establishing_aerial',
  'master_scene',
  'coverage_single',
  'coverage_two_shot',
  'reaction',
  'insert_detail',
  'cutaway_environment',
  'transition_bridge',
  'montage_element',
  'hero_product',
  'beauty_shot',
  'action_dynamic',
  'portrait_character',
  'custom'
]);
export type ShotPurpose = z.infer<typeof shotPurposeEnum>;

export const cameraAngleEnum = z.enum([
  'eye_level',
  'slightly_low',
  'low_angle',
  'extreme_low_angle',
  'worms_eye',
  'slightly_high',
  'high_angle',
  'extreme_high_angle',
  'birds_eye',
  'gods_eye',
  'dutch_tilt_subtle',
  'dutch_tilt_strong',
  'custom'
]);
export type CameraAngle = z.infer<typeof cameraAngleEnum>;

export const horizontalAngleEnum = z.enum([
  'front_straight',
  'front_quarter',
  'profile_side',
  'back_quarter',
  'back_straight',
  'custom'
]);
export type HorizontalAngle = z.infer<typeof horizontalAngleEnum>;

export const cameraMovementEnum = z.enum([
  'static_locked',
  'static_handheld_subtle',
  'pan_left_slow',
  'pan_left_medium',
  'pan_left_fast',
  'pan_right_slow',
  'pan_right_medium',
  'pan_right_fast',
  'pan_follow',
  'pan_reveal',
  'tilt_up_slow',
  'tilt_up_medium',
  'tilt_down_slow',
  'tilt_down_medium',
  'tilt_reveal',
  'dolly_in_slow',
  'dolly_in_medium',
  'dolly_in_fast',
  'dolly_out_slow',
  'dolly_out_medium',
  'dolly_out_fast',
  'push_in_dramatic',
  'pull_out_reveal',
  'truck_left_slow',
  'truck_left_medium',
  'truck_right_slow',
  'truck_right_medium',
  'truck_parallel',
  'tracking_follow_behind',
  'tracking_follow_front',
  'tracking_follow_side',
  'tracking_arc_left',
  'tracking_arc_right',
  'tracking_360',
  'tracking_orbit_slow',
  'crane_up',
  'crane_down',
  'crane_up_reveal',
  'crane_down_land',
  'boom_up',
  'boom_down',
  'handheld_subtle',
  'handheld_energetic',
  'handheld_shaky',
  'handheld_searching',
  'steadicam_float',
  'steadicam_follow',
  'gimbal_smooth',
  'whip_pan_left',
  'whip_pan_right',
  'vertigo_dolly_zoom',
  'roll_clockwise',
  'roll_counterclockwise',
  'drone_rise',
  'drone_descend',
  'drone_fly_forward',
  'drone_fly_backward',
  'drone_orbit',
  'drone_reveal',
  'drone_top_down',
  'custom'
]);
export type CameraMovement = z.infer<typeof cameraMovementEnum>;

export const movementSpeedEnum = z.enum([
  'speed_very_slow',
  'speed_slow',
  'speed_medium',
  'speed_fast',
  'speed_very_fast',
  'speed_ramping_in',
  'speed_ramping_out',
  'custom'
]);
export type MovementSpeed = z.infer<typeof movementSpeedEnum>;

export const movementMotivationEnum = z.enum([
  'follow_subject',
  'reveal_information',
  'emphasize_emotion',
  'establish_space',
  'create_energy',
  'build_tension',
  'release_tension',
  'transition_scene',
  'subjective_pov',
  'custom'
]);
export type MovementMotivation = z.infer<typeof movementMotivationEnum>;

// Lens and depth
export const focalLengthEnum = z.enum([
  'lens_ultra_wide_14mm',
  'lens_ultra_wide_18mm',
  'lens_ultra_wide_21mm',
  'lens_wide_24mm',
  'lens_wide_28mm',
  'lens_wide_35mm',
  'lens_normal_40mm',
  'lens_normal_50mm',
  'lens_short_tele_85mm',
  'lens_short_tele_100mm',
  'lens_tele_135mm',
  'lens_tele_200mm',
  'lens_tele_300mm',
  'lens_tele_400mm_plus',
  'lens_macro',
  'lens_fisheye',
  'lens_tilt_shift',
  'lens_anamorphic',
  'custom'
]);
export type FocalLength = z.infer<typeof focalLengthEnum>;

export const depthOfFieldEnum = z.enum([
  'dof_deep',
  'dof_moderate',
  'dof_shallow',
  'dof_very_shallow',
  'dof_extreme_shallow',
  'dof_split_diopter',
  'dof_rack_focus_ready',
  'custom'
]);
export type DepthOfField = z.infer<typeof depthOfFieldEnum>;

export const focusPointEnum = z.enum([
  'focus_eyes',
  'focus_face',
  'focus_subject_center',
  'focus_foreground',
  'focus_midground',
  'focus_background',
  'focus_product',
  'focus_hands',
  'focus_selective',
  'custom'
]);
export type FocusPoint = z.infer<typeof focusPointEnum>;

// Composition
export const framingGuideEnum = z.enum([
  'framing_rule_of_thirds',
  'framing_center',
  'framing_golden_ratio',
  'framing_golden_triangle',
  'framing_symmetry',
  'framing_asymmetry',
  'framing_leading_lines',
  'framing_frame_within',
  'framing_negative_space',
  'framing_fill_frame',
  'framing_diagonal',
  'framing_triangle',
  'framing_layered',
  'custom'
]);
export type FramingGuide = z.infer<typeof framingGuideEnum>;

export const subjectPositionEnum = z.enum([
  'position_center',
  'position_left_third',
  'position_right_third',
  'position_top_third',
  'position_bottom_third',
  'position_top_left',
  'position_top_right',
  'position_bottom_left',
  'position_bottom_right',
  'position_off_center_left',
  'position_off_center_right',
  'position_looking_into_frame',
  'position_looking_out_frame',
  'custom'
]);
export type SubjectPosition = z.infer<typeof subjectPositionEnum>;

export const foregroundElementEnum = z.enum([
  'foreground_none',
  'foreground_blur_soft',
  'foreground_frame_natural',
  'foreground_frame_object',
  'foreground_silhouette',
  'foreground_texture',
  'foreground_leading_line',
  'custom'
]);
export type ForegroundElement = z.infer<typeof foregroundElementEnum>;

// Environment and time
export const locationTypeEnum = z.enum([
  'interior_home_living',
  'interior_home_bedroom',
  'interior_home_kitchen',
  'interior_home_bathroom',
  'interior_office_corporate',
  'interior_office_startup',
  'interior_office_home',
  'interior_studio_white',
  'interior_studio_dark',
  'interior_studio_colored',
  'interior_retail_luxury',
  'interior_retail_casual',
  'interior_restaurant_upscale',
  'interior_restaurant_casual',
  'interior_cafe',
  'interior_bar_club',
  'interior_gym_fitness',
  'interior_warehouse',
  'interior_factory',
  'interior_hospital',
  'interior_school',
  'interior_library',
  'interior_museum',
  'interior_theater',
  'interior_hotel_lobby',
  'interior_hotel_room',
  'interior_car',
  'interior_plane',
  'interior_train',
  'exterior_street_city',
  'exterior_street_suburban',
  'exterior_sidewalk',
  'exterior_alley',
  'exterior_parking',
  'exterior_rooftop',
  'exterior_plaza',
  'exterior_park_urban',
  'exterior_bridge',
  'exterior_waterfront',
  'exterior_beach',
  'exterior_ocean',
  'exterior_lake',
  'exterior_river',
  'exterior_forest',
  'exterior_mountain',
  'exterior_desert',
  'exterior_field',
  'exterior_garden',
  'exterior_farm',
  'exterior_snow',
  'exterior_tropical',
  'exterior_building_modern',
  'exterior_building_classic',
  'exterior_building_industrial',
  'exterior_house_residential',
  'exterior_storefront',
  'custom'
]);
export type LocationType = z.infer<typeof locationTypeEnum>;

export const timeOfDayEnum = z.enum([
  'time_dawn',
  'time_golden_hour_morning',
  'time_morning_early',
  'time_morning_late',
  'time_midday',
  'time_afternoon_early',
  'time_afternoon_late',
  'time_golden_hour_evening',
  'time_sunset',
  'time_dusk',
  'time_twilight',
  'time_night_early',
  'time_night_late',
  'time_magic_hour',
  'custom'
]);
export type TimeOfDay = z.infer<typeof timeOfDayEnum>;

export const weatherConditionEnum = z.enum([
  'weather_clear_sunny',
  'weather_clear_blue_sky',
  'weather_partly_cloudy',
  'weather_overcast',
  'weather_fog_light',
  'weather_fog_heavy',
  'weather_mist',
  'weather_haze',
  'weather_rain_light',
  'weather_rain_heavy',
  'weather_storm',
  'weather_snow_light',
  'weather_snow_heavy',
  'weather_wind',
  'weather_dust',
  'custom'
]);
export type WeatherCondition = z.infer<typeof weatherConditionEnum>;

export const seasonEnum = z.enum([
  'season_spring_early',
  'season_spring_late',
  'season_summer_early',
  'season_summer_peak',
  'season_summer_late',
  'season_autumn_early',
  'season_autumn_peak',
  'season_autumn_late',
  'season_winter_early',
  'season_winter_peak',
  'season_winter_late',
  'season_dry',
  'season_wet',
  'season_monsoon',
  'custom'
]);
export type Season = z.infer<typeof seasonEnum>;

// Subject and character
export const subjectTypeEnum = z.enum([
  'subject_single_person',
  'subject_couple',
  'subject_small_group',
  'subject_large_group',
  'subject_crowd',
  'subject_adult_male',
  'subject_adult_female',
  'subject_child',
  'subject_teenager',
  'subject_elderly',
  'subject_baby',
  'subject_family',
  'subject_professional',
  'subject_athlete',
  'subject_model',
  'subject_product_single',
  'subject_product_group',
  'subject_food_dish',
  'subject_food_ingredients',
  'subject_vehicle',
  'subject_architecture',
  'subject_landscape',
  'subject_animal',
  'subject_abstract',
  'subject_text_graphic',
  'custom'
]);
export type SubjectType = z.infer<typeof subjectTypeEnum>;

export const wardrobeStyleEnum = z.enum([
  'wardrobe_formal_business',
  'wardrobe_business_casual',
  'wardrobe_smart_casual',
  'wardrobe_casual_everyday',
  'wardrobe_streetwear',
  'wardrobe_athletic_sport',
  'wardrobe_outdoor_adventure',
  'wardrobe_luxury_designer',
  'wardrobe_minimalist_clean',
  'wardrobe_bohemian',
  'wardrobe_vintage_retro',
  'wardrobe_uniform_work',
  'wardrobe_medical',
  'wardrobe_hospitality',
  'wardrobe_creative_artistic',
  'custom'
]);
export type WardrobeStyle = z.infer<typeof wardrobeStyleEnum>;

export const characterExpressionEnum = z.enum([
  'expression_neutral',
  'expression_subtle_smile',
  'expression_genuine_smile',
  'expression_laughing',
  'expression_joyful',
  'expression_excited',
  'expression_surprised_positive',
  'expression_proud',
  'expression_confident',
  'expression_content',
  'expression_peaceful',
  'expression_loving',
  'expression_hopeful',
  'expression_focused',
  'expression_contemplative',
  'expression_curious',
  'expression_listening',
  'expression_determined',
  'expression_serious',
  'expression_professional',
  'expression_intense',
  'expression_passionate',
  'expression_dramatic',
  'expression_concerned',
  'expression_worried',
  'expression_sad',
  'expression_frustrated',
  'expression_tired',
  'expression_surprised_shocked',
  'custom'
]);
export type CharacterExpression = z.infer<typeof characterExpressionEnum>;

export const characterPoseEnum = z.enum([
  'pose_standing_straight',
  'pose_standing_relaxed',
  'pose_standing_confident',
  'pose_standing_casual',
  'pose_leaning',
  'pose_arms_crossed',
  'pose_hands_in_pockets',
  'pose_hands_on_hips',
  'pose_sitting_formal',
  'pose_sitting_relaxed',
  'pose_sitting_casual',
  'pose_sitting_lean_forward',
  'pose_sitting_lean_back',
  'pose_walking',
  'pose_running',
  'pose_reaching',
  'pose_gesturing',
  'pose_pointing',
  'pose_holding_object',
  'pose_using_device',
  'pose_working',
  'pose_writing',
  'pose_conversation',
  'pose_handshake',
  'pose_presenting',
  'pose_looking_at_camera',
  'pose_looking_away',
  'pose_profile',
  'custom'
]);
export type CharacterPose = z.infer<typeof characterPoseEnum>;

export const eyeDirectionEnum = z.enum([
  'eyes_to_camera',
  'eyes_slight_off_camera',
  'eyes_looking_left',
  'eyes_looking_right',
  'eyes_looking_up',
  'eyes_looking_down',
  'eyes_closed',
  'eyes_at_other_person',
  'eyes_at_object',
  'eyes_distant_thoughtful',
  'custom'
]);
export type EyeDirection = z.infer<typeof eyeDirectionEnum>;

// Video-specific
export const motionIntensityEnum = z.enum([
  'motion_minimal',
  'motion_subtle',
  'motion_moderate',
  'motion_active',
  'motion_dynamic',
  'motion_intense',
  'custom'
]);
export type MotionIntensity = z.infer<typeof motionIntensityEnum>;

export const subjectMotionEnum = z.enum([
  'subject_static',
  'subject_subtle_movement',
  'subject_gesture',
  'subject_head_turn',
  'subject_walking',
  'subject_walking_toward',
  'subject_walking_away',
  'subject_running',
  'subject_jumping',
  'subject_dancing',
  'subject_working',
  'subject_interacting',
  'custom'
]);
export type SubjectMotion = z.infer<typeof subjectMotionEnum>;

export const videoDurationEnum = z.enum([
  'duration_2s',
  'duration_3s',
  'duration_4s',
  'duration_5s',
  'duration_6s',
  'duration_8s',
  'duration_10s',
  'custom'
]);
export type VideoDuration = z.infer<typeof videoDurationEnum>;

export const frameRateStyleEnum = z.enum([
  'fps_cinematic_24',
  'fps_broadcast_30',
  'fps_smooth_60',
  'fps_slow_motion_ready',
  'fps_timelapse',
  'custom'
]);
export type FrameRateStyle = z.infer<typeof frameRateStyleEnum>;

export const sceneTransitionEnum = z.enum([
  'transition_hard_cut',
  'transition_match_cut',
  'transition_jump_cut',
  'transition_l_cut',
  'transition_j_cut',
  'transition_smash_cut',
  'transition_dissolve_slow',
  'transition_dissolve_medium',
  'transition_dissolve_fast',
  'transition_cross_dissolve',
  'transition_fade_to_black',
  'transition_fade_from_black',
  'transition_fade_to_white',
  'transition_fade_from_white',
  'transition_wipe_left',
  'transition_wipe_right',
  'transition_wipe_up',
  'transition_wipe_down',
  'transition_iris_in',
  'transition_iris_out',
  'transition_whip_pan',
  'transition_blur',
  'transition_zoom_in',
  'transition_zoom_out',
  'transition_morph',
  'transition_last_frame_first_frame',
  'custom'
]);
export type SceneTransition = z.infer<typeof sceneTransitionEnum>;

// Format
export const aspectRatioEnum = z.enum([
  'aspect_16_9',
  'aspect_1_85_1',
  'aspect_2_35_1',
  'aspect_2_39_1',
  'aspect_21_9',
  'aspect_4_3',
  'aspect_3_2',
  'aspect_9_16',
  'aspect_4_5',
  'aspect_1_1',
  'custom'
]);
export type AspectRatioPreset = z.infer<typeof aspectRatioEnum>;

export const resolutionEnum = z.enum([
  'resolution_720p',
  'resolution_1080p',
  'resolution_2k',
  'resolution_4k',
  'resolution_4k_cinema',
  'custom'
]);
export type ResolutionPreset = z.infer<typeof resolutionEnum>;

// Visual bible defaults (UI helpers)
export const defaultVisualBible = {
  stylePreset: 'commercial_lifestyle' as VisualStylePreset,
  colorPalette: 'neutral_balanced' as ColorPalette,
  moodAtmosphere: 'mood_professional' as MoodAtmosphere,
  lightingSetup: 'natural_window_soft' as LightingSetup,
  lightQuality: 'quality_soft' as LightQuality
};

export const defaultSceneImageSettings = {
  shotSize: 'medium_shot' as ShotSize,
  cameraAngle: 'eye_level' as CameraAngle,
  depthOfField: 'dof_moderate' as DepthOfField,
  framingGuide: 'framing_rule_of_thirds' as FramingGuide
};

export const defaultSceneVideoSettings = {
  cameraMovement: 'static_locked' as CameraMovement,
  motionIntensity: 'motion_subtle' as MotionIntensity,
  durationSec: 4,
  movementSpeed: 'speed_slow' as MovementSpeed
};
